import { useCallback, useState } from "react"
import { useRouter } from "next/router"

import {
  useTestSetCloneAction,
  useTestSetDeleteAction,
} from "@/test-set/actions"
import {
  isDuplicateTestSetNameError,
  isPersistedTestSetId,
  mapUpdateTestSetInputToTransport,
  useInvalidateTestSetTransportQueries,
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
  useUpdateTestSetTransport,
} from "@/test-set/transport"
import { toast } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useQueryParams } from "@/hooks/useQueryParams"
import { downloadBlob } from "@/utils/download"

import { EVENTS, useEventDispatcher } from "../event-bus"
import { useLiveTestSetDisplayName } from "../hooks/useLiveTestSetEditorState"
import { buildTestSetCsvContent } from "./testSetCsvExport"

export const useTestSetBreadcrumb = (providedTestSetId?: string) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const dispatch = useEventDispatcher()
  const { trackEvent } = useAnalytics()

  const isTestSetDetailPage =
    router.pathname === "/[slug]/test-sets/[id]" ||
    router.pathname === "/[slug]/test-sets/[id]/index"
  const testSetId =
    providedTestSetId ||
    (isTestSetDetailPage ? (router.query.id as string | undefined) : undefined)

  const { data: testSetMetadata } = useTestSetMetadataTransport(
    testSetId || "",
    { enabled: !!testSetId },
  )

  const {
    data: records,
    isLoading: recordsIsLoading,
    isError: recordsIsError,
    error: recordsError,
  } = useTestSetRecordsTransport(
    { test_set_id: testSetId || "" },
    { enabled: !!testSetId },
  )

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [newTestSetName, setNewTestSetName] = useState("")

  const updateTestSetMutation = useUpdateTestSetTransport()

  const isPersisted = isPersistedTestSetId(testSetId)

  const liveDisplayName = useLiveTestSetDisplayName(
    isPersisted ? (testSetId ?? "") : "",
  )
  const currentDisplayName =
    liveDisplayName ?? testSetMetadata?.metadata?.display_name
  const knownTestCaseCount =
    records?.length ?? testSetMetadata?.metadata?.test_case_count

  const { cloneTestSet, isCloning } = useTestSetCloneAction({
    // The editor leaves for the list once the copy exists.
    onCloned: useCallback(() => {
      const slug = router.query.slug as string
      void router.push(queryParams.getHref(`/test-sets`, slug))
    }, [queryParams, router]),
  })

  const handleCloneTestSet = useCallback(
    (): Promise<void> =>
      cloneTestSet({ testSetId, testCaseCount: knownTestCaseCount }),
    [cloneTestSet, knownTestCaseCount, testSetId],
  )

  const handleDeleteTestSet = useCallback(() => {
    if (!testSetId) {
      return
    }
    setIsDeleteModalOpen(true)
  }, [testSetId])

  const { deleteTestSet, isDeleting } = useTestSetDeleteAction({
    onDeleted: useCallback(() => {
      setIsDeleteModalOpen(false)
      const slug = router.query.slug as string
      void router.push(queryParams.getHref(`/test-sets`, slug))
    }, [queryParams, router]),
  })

  const handleConfirmDelete = useCallback(
    (): Promise<void> => deleteTestSet(testSetId),
    [deleteTestSet, testSetId],
  )

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const handleDownload = useCallback(() => {
    if (!records || !testSetMetadata?.metadata?.display_name) {
      return
    }

    trackEvent(TRACKING_EVENTS.TEST_SET.CSV_DOWNLOADED, {
      test_set_id: testSetId,
      test_case_count: records.length,
    })

    const csvContent = buildTestSetCsvContent(records)
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })

    downloadBlob(blob, `${testSetMetadata.metadata.display_name}.csv`)
  }, [records, testSetMetadata?.metadata?.display_name, trackEvent, testSetId])

  const handleOpenRename = useCallback(() => {
    if (!testSetId) {
      return
    }
    setNewTestSetName("")
    setIsRenameModalOpen(true)
  }, [testSetId])

  const handleCancelRename = useCallback(() => {
    setIsRenameModalOpen(false)
    setNewTestSetName("")
  }, [])

  const handleConfirmRename = useCallback(async (): Promise<void> => {
    if (!testSetId || !newTestSetName.trim()) {
      return
    }

    const trimmedName = newTestSetName.trim()

    if (trimmedName === currentDisplayName) {
      setIsRenameModalOpen(false)
      setNewTestSetName("")
      return
    }

    if (!isPersisted) {
      dispatch(EVENTS.METADATA_UPDATE, { displayName: trimmedName })
      setIsRenameModalOpen(false)
      setNewTestSetName("")
      return
    }

    const MAX_RETRIES = 10
    let finalName = trimmedName

    try {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          await updateTestSetMutation.mutateAsync(
            mapUpdateTestSetInputToTransport({
              testSetId,
              displayName: finalName,
            }),
          )
          break
        } catch (error) {
          if (!isDuplicateTestSetNameError(error) || attempt === MAX_RETRIES) {
            throw error
          }
          finalName = `${finalName.replace(/ \(\d+\)$/, "")} (${attempt + 1})`
        }
      }

      trackEvent(TRACKING_EVENTS.TEST_SET.RENAMED, {
        test_set_id: testSetId,
      })
      toast.success({
        title: "Success",
        description: "Test set renamed successfully",
      })
      dispatch(EVENTS.METADATA_UPDATE, { displayName: finalName })
      await invalidateTestSetTransportQueries()
      setIsRenameModalOpen(false)
      setNewTestSetName("")
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update test set name"

      trackEvent(TRACKING_EVENTS.TEST_SET.NAME_UPDATE_FAILED, {
        error: errorMessage,
        testSetId,
      })

      toast.danger({
        title: "Update Failed",
        description: errorMessage,
      })
    }
  }, [
    testSetId,
    isPersisted,
    newTestSetName,
    currentDisplayName,
    updateTestSetMutation,
    dispatch,
    invalidateTestSetTransportQueries,
    trackEvent,
  ])

  const handleOpenVersionHistory = useCallback(() => {
    dispatch(EVENTS.OPEN_VERSION_HISTORY, {})
  }, [dispatch])

  return {
    isTestSetDetailPage,
    testSetId,
    testSetMetadata,
    isCloning,
    isDeleting,
    isPersisted,
    isRenaming: updateTestSetMutation.isPending,
    handleOpenVersionHistory,
    currentDisplayName,
    records,
    recordsIsLoading,
    recordsIsError,
    recordsError,
    handleCloneTestSet,
    handleDownload,
    handleDeleteTestSet,
    handleConfirmDelete,
    handleCancelDelete,
    isDeleteModalOpen,
    isRenameModalOpen,
    newTestSetName,
    setNewTestSetName,
    handleOpenRename,
    handleCancelRename,
    handleConfirmRename,
  }
}
